import { useEffect, useMemo, useState } from "react"

import { useParams } from "react-router-dom"

import { GradeBadge } from "@l2r-front/l2r-common"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { SpeedingsIcon } from "@l2r-front/l2r-icons"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { InfoItemButton } from "@l2r-front/l2r-ui"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { SPEEDINGS_DEFAULT_ACTIVE_LAYER } from "../../constants/urlParams"
import { useSpeedingsContext } from "../../contexts/SpeedingsContext"
import { useSpeedingsGrades } from "../../hooks/queries/useSpeedingsGrades"
import { useSpeedingsProject } from "../../hooks/queries/useSpeedingsProject"
import { speedingsLegend } from "../../utils/getSpeedingsConfig"
import { getGradeFromValue } from "../../utils/valueUtils"

import * as Styled from "./SpeedLimitsSelector.styled"

export const SpeedLimitsSelector = (props) => {

    const {
        className,
        ...infoItemProps
    } = props

    const { road } = useParams()
    const { data } = useSpeedingsGrades({
        group_by: "year",
        ...road && { linear_location_road: road },
    })
    const { data: allYearsGrades } = useSpeedingsGrades({
        group_by: ["speed_limit", "year"],
        ...road && { linear_location_road: road },
    })

    const { data: projectData } = useSpeedingsProject()
    const [state, dispatch] = useSpeedingsContext()
    const { currentActiveLayer, currentYear } = state
    const { setCurrentActiveLayer } = dispatch
    const { t } = useTranslation(I18N_NAMESPACE)
    const [isOpen, setIsOpen] = useState(false)

    const grade = useMemo(() => {
        if (!data || !currentYear) {
            return null
        }
        const currentYearData = data.find(dataItem => {
            return parseInt(dataItem.year) === parseInt(currentYear)
        })
        return getGradeFromValue(currentYearData?.weighted_avg)
    }, [currentYear, data])

    const currentYearHasData = useMemo(() => {
        if (!allYearsGrades || !currentYear) {
            return false
        }
        const currentYearData = allYearsGrades.find(dataItem => {
            return dataItem.year === currentYear
        })
        return currentYearData?.weighted_avg !== undefined
    }, [currentYear, allYearsGrades])

    const expandableChildren = useMemo(() => {
        const speedLimits = projectData?.speed_limits

        if (speedLimits && allYearsGrades) {
            return speedLimits.map(speedLimit => {
                const currentLimitGrade = allYearsGrades.find(dataItem => {
                    return dataItem.speed_limit === speedLimit && dataItem.year === currentYear
                })
                return <InfoItemButton
                    disabled={!currentLimitGrade && currentActiveLayer !== speedLimit.toString()}
                    endAdornments={<Styled.ChildrenGradeBadge
                        disabled={!currentLimitGrade && currentActiveLayer !== speedLimit.toString()}
                        grade={getGradeFromValue(currentLimitGrade?.weighted_avg ?? null)}
                        id={`speedLimits-${speedLimit.speed_limit}`}
                        legend={speedingsLegend}
                    />}
                    key={speedLimit.toString()}
                    label={t(I18N_NAMESPACE, "containers.speedLimitsSelector.label", { speedLimit })}
                    onClick={() => setCurrentActiveLayer(`${speedLimit}`)}
                    selected={currentActiveLayer === speedLimit.toString()}
                />
            })
        }
        return null
    }, [allYearsGrades, currentActiveLayer, currentYear, projectData, setCurrentActiveLayer, t])

    useEffect(() => {
        const isExpendableChildrenSelected = projectData?.speed_limits.some(speedLimit => {
            return speedLimit.toString() === currentActiveLayer
        })
        if (isExpendableChildrenSelected) {
            setIsOpen(true)
        } else {
            setIsOpen(false)
        }
    }, [currentActiveLayer, currentYear, projectData?.speed_limits, road])

    const handleClick = () => {
        if (currentActiveLayer === SPEEDINGS_DEFAULT_ACTIVE_LAYER) {
            setIsOpen(!isOpen)
        }
        setCurrentActiveLayer(SPEEDINGS_DEFAULT_ACTIVE_LAYER)
    }

    return (
        <InfoItemButton
            endAdornments={<GradeBadge
                grade={grade}
                id="speedLimits"
                legend={speedingsLegend}
            />}
            className={className}
            disabled={!currentYearHasData}
            icon={SpeedingsIcon}
            isOpen={isOpen}
            label={t(I18N_NAMESPACE, "containers.speedLimitsSelector.labelDefault")}
            onClick={handleClick}
            selected={currentActiveLayer === SPEEDINGS_DEFAULT_ACTIVE_LAYER}
            tooltipText={t(I18N_NAMESPACE, "containers.speedLimitsSelector.tooltip")}
            {...infoItemProps}
        >
            {expandableChildren}
        </InfoItemButton>
    )
}

SpeedLimitsSelector.propTypes = InfoItemButton.propTypes & {
    className: PropTypes.string,
}